import React from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { isOfType, unreachable } from '~/ts-utils';
import { EmptyRatings } from '../empty-ratings/empty-ratings';
import { ReadyRatings } from '../ready-ratings/ready-ratings';
import { Placeholder } from '../placeholder/placeholder';
import { classes, st } from './app.st.css';
import { ERROR, IDLE, PENDING, READY, ROOT, ROOT_CSR_READY } from '~ratings/constants/data-hooks';
import classNames from 'classnames';
import { RatingState } from '~commons/common-types';
import { useSlotContext } from '@wix/widget-plugins-ooi-context';
import { useApi } from '~ratings/Widget/components/api-provider/use-api';
import settingsParams from '~ratings/settingsParams';
import { useSettings } from '@wix/tpa-settings/react';
import { Alignment } from '~ratings/common-types';

const parseAlignment = (alignment: Alignment) => {
  switch (alignment) {
    case 'start':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'end':
      return 'flex-end';
    default:
      console.warn(`Alignment property ${alignment} not supported!`);
      return 'flex-start';
  }
};

export const App: React.FC = () => {
  const { isRTL, isSSR, isEditor } = useEnvironment();

  const slotContext = useSlotContext();
  const settings = useSettings();

  const [ref, setRootRef] = React.useState<HTMLElement | null>(null);
  const { ratingState, alignment } = useApi<{ ratingState: RatingState; alignment: Alignment }>(
    (ctx) => {
      return {
        ratingState: (() => {
          switch (ctx.type) {
            case 'single_state':
              return ctx.ratingState;
            case 'multi_state':
            default:
              if (slotContext?.type === 'initialized' && ref) {
                const { resourceId } = slotContext.value;
                const state = ctx.state.ratingStates?.[resourceId as string];
                if (state) {
                  return {
                    ...state,
                    focusReviewsComponent: () =>
                      ctx.actions.focusReviewsComponent(resourceId as string),
                  };
                }
              }
              return {
                type: 'IDLE' as const,
              };
          }
        })(),
        alignment: ctx.type === 'single_state' ? ctx.alignment ?? 'start' : 'start',
      };
    },
  );

  return (
    <div
      data-hook={classNames(ROOT, {
        [READY]: isOfType(['READY', 'READY_EMPTY'], ratingState),
        [PENDING]: isOfType(['PENDING'], ratingState),
        [ERROR]: isOfType(['ERROR'], ratingState),
        [IDLE]: isOfType(['IDLE'], ratingState),
        [ROOT_CSR_READY]: !isSSR && ref,
      })}
      className={st(classes.root, {
        isEmpty:
          ratingState.type === 'READY_EMPTY' && settings.get(settingsParams.emptyState) === 'blank',
        isEditor,
      })}
      dir={isRTL ? 'rtl' : 'ltr'}
      ref={setRootRef}
      style={{ justifyContent: parseAlignment(alignment) }}
    >
      {(() => {
        switch (ratingState.type) {
          case 'READY':
            return (
              <ReadyRatings
                overall={ratingState.overall}
                totalReviews={ratingState.totalReviews}
                onClick={ratingState.focusReviewsComponent}
              />
            );
          case 'READY_EMPTY':
            return <EmptyRatings />;
          case 'ERROR':
          case 'IDLE':
          case 'PENDING':
            return <Placeholder />;
          default:
            throw unreachable(ratingState);
        }
      })()}
    </div>
  );
};
